import axios from 'axios';

const createLotusBlossomApiInstance = () => {
  const baseURL =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:10000/api3.2/lotus-blossom'
      : '/api3.2/lotus-blossom';

  return axios.create({
    baseURL,
    withCredentials: true,
    // Paging Stripe takes noticeably longer than a database read.
    timeout: 60000,
  });
};

const lotusBlossomApi = createLotusBlossomApiInstance();

/**
 * On localhost the base URL is the API server, whose host says nothing about the
 * tenant — it would resolve to whichever database the dev port maps to, and
 * these endpoints refuse anyone but Lotus Blossom. Deployed builds use the
 * relative base URL and never reach this.
 */
const LOCAL_DEV_TENANT_HOST = 'lotusblossom.dev.streamdata.com';

const getForwardedHost = (apiUrl: string | undefined): string => {
  if (!apiUrl || apiUrl.startsWith('/')) return window.location.host;

  try {
    const parsed = new URL(apiUrl);
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
      return LOCAL_DEV_TENANT_HOST;
    }
    return parsed.host || window.location.host;
  } catch {
    return window.location.host;
  }
};

/**
 * The tenant host these endpoints resolve by, for CANmail calls that have to land
 * on the same database.
 *
 * The session list ids come from the legacy /api2.0 backend against Lotus
 * Blossom's database, but CANmail's own /api3.2 client derives its host from its
 * base URL — which on localhost is `localhost:10000`, a different tenant. The id
 * is then looked up in the wrong database, where it belongs to an unrelated list,
 * so the roster comes back populated and wrong rather than empty.
 *
 * Deployed this is just `window.location.host`, which is what CANmail would have
 * sent anyway — the divergence only exists in local dev.
 */
export const getLotusBlossomForwardedHost = (): string =>
  getForwardedHost(lotusBlossomApi.defaults.baseURL);

export interface LotusBlossomPurchase {
  kind: 'payment' | 'subscription';
  /** ISO timestamp: when the payment was made, or the subscription started. */
  date: string;
  /** Smallest currency unit, as Stripe reports it. */
  amount: number;
  currency: string;
  status: string;
  /** Empty for subscriptions, which aren't tied to a theme. */
  themeName: string;
  /** "YYYY-MM-DD"; empty on older purchases that carried no date metadata. */
  themeStartDate: string;
  /**
   * Subscriptions only: ISO timestamp the billing period ends — the day of the
   * next charge, or of the last day of access when `cancelAtPeriodEnd`. Empty
   * on every other row, on an ended membership, and on responses from a backend
   * that predates this field.
   */
  periodEnd?: string;
  /** Subscriptions only: set to stop at `periodEnd` instead of renewing. */
  cancelAtPeriodEnd?: boolean;
  /**
   * Subscriptions only: Stripe's id, which cancel and resume take. Absent from a
   * backend that predates it, and then the membership can't be changed here.
   */
  subscriptionId?: string;
}

export type PurchasesByEmail = Record<string, LotusBlossomPurchase[]>;

export interface SyncSessionListsResult {
  /** Lists created for sessions that had none. */
  created: number;
  /** Member seats added across those lists. */
  synced: number;
  /**
   * Names of session lists that couldn't be created or synced; the others still
   * were. Absent from a backend that predates it.
   */
  failed?: string[];
}

/**
 * Creates the mail list for any upcoming session that has none and syncs current
 * members onto it, so a month booked only by members still shows up. Idempotent.
 *
 * The schedule it works from lives in the backend, so this needs nothing from
 * the caller. POST /api3.2/lotus-blossom/sync-session-lists
 */
export const SyncSessionLists = async (): Promise<SyncSessionListsResult> => {
  const { data } = await lotusBlossomApi.post(
    '/sync-session-lists',
    {},
    {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'X-Forwarded-Host': getForwardedHost(lotusBlossomApi.defaults.baseURL),
      },
    }
  );

  if (!data.success) {
    throw new Error(data.error?.message || 'Failed to sync session lists');
  }

  return data.data;
};

/**
 * Every purchase, keyed by lowercased email, newest first.
 * GET /api3.2/lotus-blossom/purchases
 */
export const GetLotusBlossomPurchases = async (): Promise<PurchasesByEmail> => {
  const { data } = await lotusBlossomApi.get('/purchases', {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      'X-Forwarded-Host': getForwardedHost(lotusBlossomApi.defaults.baseURL),
    },
  });

  if (!data.success) {
    throw new Error(data.error?.message || 'Failed to load purchase history');
  }

  return data.data.by_email ?? {};
};

/** One session as the published schedule lists it; only what the admin page reads. */
export interface ScheduledSession {
  /** "YYYY-MM-DD" */
  startDate: string;
  /** "YYYY-MM-DD", the session's last day. */
  endDate: string;
  /** e.g. "Breaking the Loop, Integration" — the text its mail list is named after. */
  title: string;
}

/**
 * Every session in the published calendar. Mail list names carry only a
 * session's first day; this is where its last day comes from.
 *
 * The same public endpoint the storefront renders, so it sits on /noauth rather
 * than under this client's /lotus-blossom base URL.
 * GET /api3.2/noauth/lotus-blossom/schedule
 */
export const GetLotusBlossomSchedule = async (): Promise<ScheduledSession[]> => {
  const base = (lotusBlossomApi.defaults.baseURL ?? '').replace(/\/lotus-blossom$/, '');
  const { data } = await axios.get(`${base}/noauth/lotus-blossom/schedule`, {
    timeout: 15000,
    headers: {
      'X-Forwarded-Host': getForwardedHost(lotusBlossomApi.defaults.baseURL),
    },
  });

  if (!data.success || !Array.isArray(data.data?.months)) {
    throw new Error(data.error?.message || 'Failed to load the session schedule');
  }

  return data.data.months.flatMap(
    (month: { sessions?: ScheduledSession[] }) => month.sessions ?? []
  );
};

/** The API's own message where there is one; axios's says only the status. */
const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
};

const changeMembership = async (
  subscriptionId: string,
  action: 'cancel' | 'resume',
  fallbackError: string
): Promise<LotusBlossomPurchase> => {
  try {
    const { data } = await lotusBlossomApi.post(
      `/memberships/${encodeURIComponent(subscriptionId)}/${action}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          'X-Forwarded-Host': getForwardedHost(lotusBlossomApi.defaults.baseURL),
        },
      }
    );

    if (!data.success) throw new Error(data.error?.message || fallbackError);
    return data.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, fallbackError));
  }
};

/**
 * Stops a membership renewing; it ends with the period already paid for.
 * POST /api3.2/lotus-blossom/memberships/:subscriptionId/cancel
 */
export const CancelLotusBlossomMembership = (subscriptionId: string) =>
  changeMembership(subscriptionId, 'cancel', 'Failed to cancel the membership');

/**
 * Undoes a scheduled cancellation before it takes effect.
 * POST /api3.2/lotus-blossom/memberships/:subscriptionId/resume
 */
export const ResumeLotusBlossomMembership = (subscriptionId: string) =>
  changeMembership(subscriptionId, 'resume', 'Failed to resume the membership');
